import { useEffect, useRef, useState } from 'react';

/**
 * Returns [ref, visible] — once the observed element enters the viewport at
 * the given threshold (and rootMargin), `visible` flips to true and stays
 * true. Used to trigger the shared `featureReveal` CSS animation.
 *
 * Options:
 *   - rootMargin: standard IntersectionObserver rootMargin string (e.g.
 *     '0px 0px -15% 0px' to require the element to be 15% into the viewport
 *     before firing).
 *
 * Respects `prefers-reduced-motion`: returns visible=true immediately so the
 * element renders in its final state without animation.
 */
export default function useRevealOnScroll(threshold = 0.1, options = {}) {
  const { rootMargin = '0px' } = options;
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (typeof window !== 'undefined' &&
        window.matchMedia &&
        window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setVisible(true);
      return;
    }

    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          obs.disconnect();
        }
      },
      { threshold, rootMargin }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold, rootMargin]);

  return [ref, visible];
}
